import React from 'react';
import SEO from '../components/common/SEO';

const phases = [
  {
    num: '01',
    title: 'Strategic Discovery',
    desc: 'We map your existing manual workflows, identifying the highest-volume, most repetitive tasks where AI agents can deliver maximum ROI.',
    points: ['Workflow mapping', 'ROI assessment', 'Security requirements review']
  },
  {
    num: '02',
    title: 'Agent Design & Harness Configuration',
    desc: 'Our experts design the agentic logic and configure the Eagentix harness with your specific enterprise policies and guardrails.',
    points: ['Agent role definition', 'Policy-as-code setup', 'System integration plan']
  },
  {
    num: '03',
    title: 'Controlled Pilot',
    desc: 'Deploy agents in a controlled "sandbox" environment, validating outputs and refining the human-in-the-loop steering paths.',
    points: ['Model evaluation (LLM-as-Judge)', 'Human feedback loop setup', 'Integration testing']
  },
  {
    num: '04',
    title: 'Enterprise Rollout',
    desc: 'Scale agent clusters across your production environment with full audit trails, real-time monitoring, and expert escalation paths.',
    points: ['Production deployment', 'Team onboarding', 'Continuous performance monitoring']
  }
];

export default function ImplementationApproach() {
  const sectionLight = { padding: '80px 0', background: '#f8fafc' };
  const sectionWhite = { padding: '80px 0', background: 'white' };
  const sectionDark = { padding: '80px 0', background: '#0f172a' };
  const container = { maxWidth: '1100px', margin: '0 auto', padding: '0 24px' };
  const card = { borderRadius: '20px', border: '1px solid rgba(148,163,184,0.16)', background: 'white', padding: '28px 32px' };

  return (
    <>
      <SEO
        title="AI Implementation Approach | Eagentix"
        description="Eagentix follows a strategy-first approach to enterprise AI agent implementation, ensuring secure, compliant, and high-ROI automation."
        pathname="/implementation-approach"
      />

      {/* Hero */}
      <section style={{ ...sectionDark, paddingTop: '120px' }}>
        <div style={container}>
          <div style={{ maxWidth: '800px' }}>
            <span style={{ color: '#60a5fa', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.15em' }}>
              Methodology
            </span>
            <h1 style={{ fontSize: '3rem', fontWeight: 800, color: 'white', letterSpacing: '-0.03em', margin: '16px 0 24px' }}>
              Our Implementation Approach
            </h1>
            <p style={{ fontSize: '1.2rem', color: '#cbd5e1', lineHeight: 1.75, marginBottom: '36px' }}>
              We don't just ship models; we deploy accountable digital workers. Our strategy-first methodology ensures every AI agent is aligned with your business objectives and operates within strict enterprise guardrails.
            </p>
          </div>
        </div>
      </section>

      {/* Process Phases */}
      <section style={sectionLight}>
        <div style={container}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '32px' }}>
            {phases.map((phase, i) => (
              <div key={i} style={card}>
                <div style={{ fontSize: '2.5rem', fontWeight: 900, color: '#e2e8f0', marginBottom: '8px', lineHeight: 1 }}>{phase.num}</div>
                <h3 style={{ fontSize: '1.5rem', fontWeight: 800, color: '#0f172a', marginBottom: '16px' }}>{phase.title}</h3>
                <p style={{ color: '#475569', fontSize: '1.05rem', lineHeight: 1.7, marginBottom: '20px' }}>{phase.desc}</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {phase.points.map((p, j) => (
                    <span key={j} style={{ background: '#eff6ff', color: '#0071e3', padding: '6px 12px', borderRadius: '8px', fontSize: '0.85rem', fontWeight: 700 }}>
                      {p}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Strategy First? */}
      <section style={sectionWhite}>
        <div style={container}>
          <div style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto' }}>
            <h2 style={{ fontSize: '2.5rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', marginBottom: '24px' }}>
              Why Strategy-First?
            </h2>
            <p style={{ color: '#475569', fontSize: '1.15rem', lineHeight: 1.8, marginBottom: '32px' }}>
              Most AI initiatives fail because they start with the model, not the work. Eagentix starts by understanding the professional requirements of the task, the institutional context, and the necessary safety boundaries.
            </p>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <div style={{ background: '#f8fafc', padding: '24px', borderRadius: '20px', flex: 1, minWidth: '240px' }}>
                <h4 style={{ color: '#0071e3', marginBottom: '12px' }}>Reduced Risk</h4>
                <p style={{ fontSize: '0.95rem', margin: 0 }}>Guardrails defined before deployment prevent hallucinations and policy violations.</p>
              </div>
              <div style={{ background: '#f8fafc', padding: '24px', borderRadius: '20px', flex: 1, minWidth: '240px' }}>
                <h4 style={{ color: '#0071e3', marginBottom: '12px' }}>Higher ROI</h4>
                <p style={{ fontSize: '0.95rem', margin: 0 }}>Targeted automation of high-cost workflows delivers measurable business impact faster.</p>
              </div>
              <div style={{ background: '#f8fafc', padding: '24px', borderRadius: '20px', flex: 1, minWidth: '240px' }}>
                <h4 style={{ color: '#0071e3', marginBottom: '12px' }}>Faster Adoption</h4>
                <p style={{ fontSize: '0.95rem', margin: 0 }}>Human-in-the-loop design builds trust and ensures agents augment your existing team.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={sectionDark}>
        <div style={{ ...container, textAlign: 'center' }}>
          <h2 style={{ fontSize: '2.2rem', fontWeight: 800, color: 'white', letterSpacing: '-0.03em', marginBottom: '16px' }}>
            Ready to Start Your AI Journey?
          </h2>
          <p style={{ color: '#cbd5e1', fontSize: '1.1rem', lineHeight: 1.75, marginBottom: '36px', maxWidth: '600px', margin: '0 auto 36px' }}>
            Book a strategy session to see how our implementation approach can accelerate your AI transformation securely.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a
              href="/#contact"
              style={{ background: '#0071e3', color: 'white', padding: '14px 28px', borderRadius: '12px', fontWeight: 700, textDecoration: 'none', fontSize: '1rem' }}
            >
              Book a Strategy Session
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
